import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { Plus, Trash2, Loader2, Check } from 'lucide-react'

type VehiclePricingTier = Tables<'vehicle_pricing_tiers'>

interface TieredPricingEditorProps {
  pricingId: string
  accountId: string
  vehicleId: string
  tiers: VehiclePricingTier[]
  onTiersChange: () => void
  onDisableTieredPricing: () => void
}

interface TierFormData {
  id?: string
  tier_order: number
  miles_up_to: number | null
  price_per_mile: number
}

export function TieredPricingEditor({
  pricingId,
  accountId,
  vehicleId,
  tiers,
  onTiersChange,
  onDisableTieredPricing,
}: TieredPricingEditorProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [localTiers, setLocalTiers] = useState<TierFormData[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null)
  const [deleteConfirmTier, setDeleteConfirmTier] = useState<TierFormData | null>(null)

  // Sync local tiers with props
  useEffect(() => {
    const sorted = [...tiers].sort((a, b) => a.tier_order - b.tier_order)
    setLocalTiers(
      sorted.map((t) => ({
        id: t.id,
        tier_order: t.tier_order,
        miles_up_to: t.miles_up_to,
        price_per_mile: Number(t.price_per_mile),
      }))
    )
  }, [tiers])

  // Create tier mutation
  const createTierMutation = useMutation({
    mutationFn: async (tier: Omit<TierFormData, 'id'>) => {
      const { error } = await supabase.from('vehicle_pricing_tiers').insert({
        vehicle_pricing_id: pricingId,
        account_id: accountId,
        tier_order: tier.tier_order,
        miles_up_to: tier.miles_up_to,
        price_per_mile: tier.price_per_mile,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      onTiersChange()
    },
    onError: (error) => {
      toast({ title: 'Error creating tier', description: error.message, variant: 'destructive' })
    },
  })

  // Update tier mutation
  const updateTierMutation = useMutation({
    mutationFn: async (tier: TierFormData) => {
      if (!tier.id) throw new Error('No tier id to update')
      const { error } = await supabase
        .from('vehicle_pricing_tiers')
        .update({
          tier_order: tier.tier_order,
          miles_up_to: tier.miles_up_to,
          price_per_mile: tier.price_per_mile,
        })
        .eq('id', tier.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      setIsSaving(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    },
    onError: (error) => {
      setIsSaving(false)
      toast({ title: 'Error updating tier', description: error.message, variant: 'destructive' })
    },
  })

  // Delete tier mutation
  const deleteTierMutation = useMutation({
    mutationFn: async (tierId: string) => {
      const { error } = await supabase.from('vehicle_pricing_tiers').delete().eq('id', tierId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      onTiersChange()

      // If that was the last tier, automatically disable tiered pricing
      if (localTiers.length <= 1) {
        onDisableTieredPricing()
        toast({ title: 'Tiered pricing disabled', description: 'Last tier removed.' })
      } else {
        toast({ title: 'Tier deleted' })
      }
    },
    onError: (error) => {
      toast({ title: 'Error deleting tier', description: error.message, variant: 'destructive' })
    },
  })

  // Debounced save function
  const debouncedSave = useCallback(
    (tier: TierFormData) => {
      if (!tier.id) return
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
      setIsSaving(true)
      const timeout = setTimeout(() => {
        updateTierMutation.mutate(tier)
      }, 500)
      setSaveTimeout(timeout)
    },
    [saveTimeout, updateTierMutation]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
    }
  }, [saveTimeout])

  const handleAddTier = () => {
    const maxOrder = localTiers.reduce((max, t) => Math.max(max, t.tier_order), 0)
    const lastTier = localTiers[localTiers.length - 1]

    // If the last tier is already unlimited (null), prevent adding another tier
    if (lastTier && lastTier.miles_up_to === null) {
      toast({
        title: 'Cannot add tier',
        description:
          'The last tier is already unlimited. Edit its mile limit first to add another tier after it.',
        variant: 'destructive',
      })
      return
    }

    // New tier should always be unlimited (null) since it becomes the new "catch-all" tier
    const newTier: Omit<TierFormData, 'id'> = {
      tier_order: maxOrder + 1,
      miles_up_to: null, // Always unlimited for new last tier
      price_per_mile: lastTier ? Math.max(0, lastTier.price_per_mile - 0.25) : 2.5,
    }

    createTierMutation.mutate(newTier)
  }

  const handleUpdateTier = (index: number, field: keyof TierFormData, value: unknown) => {
    const updatedTiers = [...localTiers]
    updatedTiers[index] = {
      ...updatedTiers[index],
      [field]: value,
    }
    setLocalTiers(updatedTiers)

    // Debounced save
    if (updatedTiers[index].id) {
      debouncedSave(updatedTiers[index])
    }
  }

  const handleDeleteTier = (tier: TierFormData) => {
    setDeleteConfirmTier(tier)
  }

  const confirmDelete = () => {
    if (deleteConfirmTier?.id) {
      deleteTierMutation.mutate(deleteConfirmTier.id)
    }
    setDeleteConfirmTier(null)
  }

  const getTierLabel = (index: number, tier: TierFormData): string => {
    if (index === 0) {
      if (tier.miles_up_to === null) {
        return 'All miles'
      }
      return `First ${tier.miles_up_to} miles`
    }

    const prevTier = localTiers[index - 1]
    const prevMiles = prevTier?.miles_up_to ?? 0

    if (tier.miles_up_to === null) {
      return `Miles over ${prevMiles}`
    }

    return `Miles ${prevMiles + 1}-${tier.miles_up_to}`
  }

  const isLastTier = (index: number) => index === localTiers.length - 1

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Mileage Tiers</Label>
          {isSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {saveSuccess && !isSaving && (
            <Check className="h-4 w-4 text-green-500 animate-in fade-in duration-200" />
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddTier}
          disabled={createTierMutation.isPending}
        >
          {createTierMutation.isPending ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <Plus className="mr-1 h-3 w-3" />
          )}
          Add Tier
        </Button>
      </div>

      {localTiers.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead>Miles Range</TableHead>
                <TableHead className="w-[150px]">Rate/Mile</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {localTiers.map((tier, index) => (
                <TableRow key={tier.id || index}>
                  <TableCell className="font-mono text-muted-foreground">
                    {tier.tier_order}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {index === 0 ? 'First' : 'Up to'}
                      </span>
                      <Input
                        type="number"
                        min={0.1}
                        step={1}
                        placeholder={isLastTier(index) ? '∞' : '20'}
                        value={tier.miles_up_to ?? ''}
                        onChange={(e) =>
                          handleUpdateTier(
                            index,
                            'miles_up_to',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        className="w-20"
                      />
                      <span className="text-xs text-muted-foreground">
                        {isLastTier(index) ? 'miles (empty = unlimited)' : 'miles'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <CurrencyInput
                      placeholder="0.00"
                      value={tier.price_per_mile}
                      onChange={(value) =>
                        handleUpdateTier(index, 'price_per_mile', value ?? 0)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDeleteTier(tier)}
                      disabled={deleteTierMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-4 text-center">
          <p className="text-sm text-muted-foreground">
            No pricing tiers configured. Click "Add Tier" to create your first tier.
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Tip: Set the first tier's rate to $0 to include those miles in the base fare. The last tier
        should have no mile limit to cover remaining miles.
      </p>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteConfirmTier} onOpenChange={() => setDeleteConfirmTier(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Tier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this tier from the pricing configuration. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
